/* eslint-disable @typescript-eslint/no-unnecessary-boolean-literal-compare */
/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description getPathFromFilters
 */

import { PropertyFeatures } from '@zcode/enums'
import { ON_SERVER } from '@zcode/nr/es/shared/env'
import type { Filters, FiltersState } from '@zcode/state-type'
import deepequal from 'fast-deep-equal'
import {
  FIVE_BR,
  FOUR_BR,
  MAX_FILTER_PRICE,
  MAX_NIGHTLY_FILTER_PRICE,
  MIN_FILTER_PRICE,
  STUDIO,
} from '../../constants/filterConstants'
import { FILTER_GROUP_ON_SITE_LAUNDRY } from '../../constants/filterGroupConstants'

export const getPathFromFilters = (filters: FiltersState | Filters) => {
  let url = ''
  // bedrooms may be a ImmutableList<Boolean> or an Array<int> depending on Immutable v pojo call.
  // it is simple to accommodate either
  const brs: number[] = []
  const {
    cats,
    dogs,
    features,
    minPrice,
    minPricePerBedroomPercentile,
    minNightlyPrice,
    maxPrice,
    maxPricePerBedroomPercentile,
    maxNightlyPrice,
    bedrooms,
    orAmenities,
    orFeatures,
    customMaxPrice,
  } = filters
  if (bedrooms) {
    bedrooms.forEach((value: boolean, i: number) => {
      if (value === true) {
        // iterable of booleans
        brs.push(i)
      } else if (value !== false && value !== undefined) {
        // iterable of non-booleans aka integers
        brs.push(value)
      }
    })
  }
  // bedrooms
  if (brs.length === 1) {
    const firstBr: number = brs[0]
    if (firstBr === STUDIO) {
      url += '/studios'
    } else if (firstBr === FOUR_BR || firstBr === FIVE_BR) {
      url += `/${firstBr}+beds`
    } else {
      url += `/${firstBr}-beds`
    }
  } else if (brs.length > 1) {
    const firstBr: number = brs[0]
    let lastBr: number = brs[brs.length - 1]
    if (lastBr === FOUR_BR || lastBr === FIVE_BR) {
      let i
      // search backward, find first gap.
      for (i = brs.length - 1; i >= 0 && brs[i] === lastBr; --i) {
        lastBr -= 1
      }
      if (i === -1) {
        // contiguous.
        url += firstBr === STUDIO ? '/studios+' : `/${firstBr}+beds`
      } else {
        // non-contiguous
        url += `/${brs
          .slice(0, i + 1)
          .join(',')
          .replace('0', 'studios')},${lastBr + 1}+beds`
      }
    } else {
      url += `/${brs.join(',').replace('0', 'studios')}-beds`
    }
  }

  // price
  const minPriceChanged = minPrice !== MIN_FILTER_PRICE
  const maxPriceChanged = maxPrice !== MAX_FILTER_PRICE
  if (minPrice || maxPrice) {
    const minP: number | undefined = minPrice && minPriceChanged ? minPrice : undefined
    const maxP: number | undefined =
      maxPrice && (maxPriceChanged || customMaxPrice) ? maxPrice : undefined
    if (minP && maxP) {
      url += `/price-${minP},${maxP}`
    } else if (minP) {
      url += `/over-${minP}`
    } else if (maxP) {
      url += `/under-${maxP}`
    }
  }

  // nightly price
  const minNightlyPriceChanged = minNightlyPrice !== MIN_FILTER_PRICE
  const maxNightlyPriceChanged = maxNightlyPrice !== MAX_NIGHTLY_FILTER_PRICE
  if (minNightlyPrice || maxNightlyPrice) {
    const minP = minNightlyPrice && minNightlyPriceChanged ? minNightlyPrice : undefined
    const maxP =
      maxNightlyPrice && (maxNightlyPriceChanged || customMaxPrice) ? maxNightlyPrice : undefined
    if (minP && maxP) {
      url += `/nightly-price-${minP},${maxP}`
    } else if (minP) {
      url += `/nightly-over-${minP}`
    } else if (maxP) {
      url += `/nightly-under-${maxP}`
    }
  }

  // or amenities
  const isOrAmenitiesDefined = !!orAmenities && Array.isArray(orAmenities) && orAmenities.length > 0
  if (isOrAmenitiesDefined) {
    for (const amenitiesGroup of orAmenities) {
      if (deepequal(amenitiesGroup, FILTER_GROUP_ON_SITE_LAUNDRY)) {
        url += '/on-site-laundry'
      }
    }
  }

  // pet friendly
  if (cats && dogs) {
    url += '/pet-friendly'
  } else if (cats) {
    url += '/cat-friendly'
  } else if (dogs) {
    url += '/dog-friendly'
  }

  if (features?.includes(PropertyFeatures.INCOME_RESTRICTED)) {
    url += '/low-income'
  }

  if (orFeatures?.[0]?.includes(PropertyFeatures.SPECIALS)) {
    url += '/move-in-special'
  }

  if (orFeatures?.[0]?.includes(PropertyFeatures.UTILITIES_INCLUDED)) {
    url += '/utilities-included'
  }

  if (orFeatures?.[0]?.includes(PropertyFeatures.NO_SECURITY_DEPOSIT)) {
    url += '/no-security-deposit'
  }

  // cheap / lux
  if (minPricePerBedroomPercentile === 'p75') {
    url += '/luxury'
  }

  if (maxPricePerBedroomPercentile === 'p50') {
    url += '/cheap'
  }

  return url
}
